/**
 * PremiumButton — editorial-grade CTA used across storefront renderers.
 *
 * Squarespace defaults:
 *   • 48px min-height
 *   • 24-32px horizontal padding
 *   • Arrow icon on the right
 *   • Hover lift + shadow
 *   • Uppercase, letter-spaced, bold label
 *
 * Two variants: `primary` (filled) and `outline`.
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';

export default function PremiumButton({
  label,
  href = '#',
  onClick,
  variant = 'primary',
  color = '#0E0E23',
  fg = '#FFFFFF',
  showArrow = true,
  className = '',
  size = 'md',   // sm | md | lg
  ...rest
}) {
  const sizes = {
    sm: 'h-10 px-5 text-[11px]',
    md: 'h-12 px-6 sm:px-8 text-[12px]',
    lg: 'h-14 px-8 sm:px-10 text-[13px]',
  };
  const base = `wb-premium-btn inline-flex items-center gap-2.5 rounded-full font-bold uppercase tracking-[0.16em] transition-all duration-300 will-change-transform ${sizes[size] || sizes.md}`;
  const style =
    variant === 'primary'
      ? { backgroundColor: color, color: fg }
      : { backgroundColor: 'transparent', color: color, border: `1.5px solid ${color}` };

  const Tag = href && !onClick ? 'a' : 'button';
  const props = href && !onClick ? { href } : { onClick, type: 'button' };

  return (
    <>
      <Tag className={`${base} ${className}`} style={style} {...props} {...rest}>
        <span>{label}</span>
        {showArrow && <ArrowRight size={14} className="wb-premium-btn-arrow" />}
      </Tag>
      {/* Local styles for hover lift + arrow slide */}
      <style>{`
        .wb-premium-btn { position: relative; box-shadow: 0 1px 0 rgba(0,0,0,0.02); }
        .wb-premium-btn:hover { transform: translate3d(0,-2px,0); box-shadow: 0 12px 30px -8px rgba(0,0,0,0.35); }
        .wb-premium-btn:hover .wb-premium-btn-arrow { transform: translateX(3px); }
        .wb-premium-btn-arrow { transition: transform 300ms cubic-bezier(0.16,1,0.3,1); }
      `}</style>
    </>
  );
}
